// ─────────────────────────────────────────────────────────────────────────────
// Data-source options for the Report Builder's right-hand panel.
//
// The /reporting API does NOT list available sources — they live in the other
// modules. This aggregates them into the four families the builder needs:
//   dashboard   → chart blocks      (dashboard cards)
//   formula     → metric blocks     (Formula Builder formulas)
//   scorecard   → metric blocks     (Balanced Scorecard domains + overall)
//   doc_insights→ table blocks      (coded themes — fixed id "codes")
//
// Each fetch is resilient: a failing module yields an empty list rather than
// breaking the whole panel.
// ─────────────────────────────────────────────────────────────────────────────

import {
  getDashboards,
  getDashboardById,
  getTabCards,
} from "../Dashboard/services/dashboard.service";
import { getScorecard } from "../BalancedScorecard/balancedScorecard.service";
import { getFormulas } from "../DocProcessing/Analyze/FormulaBuilder/formulaBuilder.api";
import type { SourceFamily } from "./reporting.service";

export interface SourceOption {
  family: SourceFamily;
  id: string;
  label: string;
  sublabel?: string;
}

export interface GroupedSources {
  dashboard: SourceOption[];
  formula: SourceOption[];
  scorecard: SourceOption[];
  doc_insights: SourceOption[];
}

const GROUP_LABELS: Record<SourceFamily, string> = {
  dashboard: "Dashboard",
  formula: "Formula",
  scorecard: "Scorecard",
  doc_insights: "Doc Insights",
};

export const groupLabel = (family: SourceFamily): string =>
  GROUP_LABELS[family];

// ── Dashboard cards ───────────────────────────────────────────────────────────

const NON_DATA_CARDS = new Set(["text_box", "image"]);

const fetchDashboardSources = async (
  projectId: string,
): Promise<SourceOption[]> => {
  try {
    const dashboards: any[] = (await getDashboards({ projectId })) ?? [];
    const perDashboard = await Promise.all(
      dashboards.map(async (d) => {
        try {
          const full = await getDashboardById(d.id);
          const tabs = full?.tabs ?? [];
          const tabCards = await Promise.all(
            tabs.map((t: any) =>
              t.cards ? Promise.resolve(t.cards) : getTabCards(t.id),
            ),
          );
          return tabCards
            .flat()
            .filter((c: any) => !NON_DATA_CARDS.has(c.card_type ?? ""))
            .map((c: any) => ({
              family: "dashboard" as const,
              id: c.id,
              label: c.title || "Untitled card",
              sublabel: d.name || undefined,
            }));
        } catch {
          return [] as SourceOption[];
        }
      }),
    );
    return perDashboard.flat();
  } catch {
    return [];
  }
};

// ── Formulas ──────────────────────────────────────────────────────────────────

const fetchFormulaSources = async (
  projectId: string,
): Promise<SourceOption[]> => {
  try {
    const res = await getFormulas(projectId, { limit: 100 });
    if (res.type !== "success") return [];
    return (res.data?.formulas ?? []).map((f) => ({
      family: "formula" as const,
      id: f.id,
      label: f.name,
      sublabel:
        f.last_result != null ? `Last result: ${f.last_result}` : undefined,
    }));
  } catch {
    return [];
  }
};

// ── Scorecard domains ─────────────────────────────────────────────────────────

const fetchScorecardSources = async (
  projectId: string,
): Promise<SourceOption[]> => {
  try {
    const sc = await getScorecard(projectId);
    if (!sc) return [];
    const overall: SourceOption = {
      family: "scorecard",
      id: "overall",
      label: "Overall score",
      sublabel: sc.title || undefined,
    };
    const domains: SourceOption[] = (sc.domains ?? []).map((d) => ({
      family: "scorecard" as const,
      id: d.id,
      label: d.name,
      sublabel: "Domain",
    }));
    return [overall, ...domains];
  } catch {
    return [];
  }
};

// ── Doc Insights (fixed) ──────────────────────────────────────────────────────

const docInsightsSources = (): SourceOption[] => [
  {
    family: "doc_insights",
    id: "codes",
    label: "Coded themes",
    sublabel: "From Document Insights",
  },
];

// ── Public API ────────────────────────────────────────────────────────────────

export const getReportSources = async (
  projectId: string,
): Promise<GroupedSources> => {
  const [dashboard, formula, scorecard] = await Promise.all([
    fetchDashboardSources(projectId),
    fetchFormulaSources(projectId),
    fetchScorecardSources(projectId),
  ]);
  return {
    dashboard,
    formula,
    scorecard,
    doc_insights: docInsightsSources(),
  };
};

/** Resolve the display label for a bound source, if we have it loaded. */
export const findSourceLabel = (
  groups: GroupedSources | null,
  family: SourceFamily | undefined,
  id: string | null | undefined,
): string | null => {
  if (!groups || !family || !id) return null;
  const list = groups[family] ?? [];
  return list.find((o) => o.id === id)?.label ?? null;
};
